import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowDown, ArrowUpRight } from 'lucide-react';
import { PageHero } from '../components/layout/PageHero';
import { DomainCard } from '../components/demoCenter/DomainCard';
import { DomainDetailDialog } from '../components/demoCenter/DomainDetailDialog';
import { GovernmentFeature } from '../components/demoCenter/GovernmentFeature';
import { DemoFoundation } from '../components/demoCenter/DemoFoundation';
import { demoDomains, demoStatusLabels, governmentDemoUrl, type DemoDomain } from '../data/demoCenter';
import { fadeUp } from '../utils/motion';
export function NoetivraDemoCenterPage() {
  const [openDomain, setOpenDomain] = useState<DemoDomain | null>(null);
  const [highlightedId, setHighlightedId] = useState<DemoDomain['id'] | null>(null);

  // A domain anchor (e.g. #talent) highlights its card; planned domains also open their detail.
  useEffect(() => {
    const hash = window.location.hash.slice(1);
    const domain = demoDomains.find(item => item.id === hash);
    if (!domain) return;
    setHighlightedId(domain.id);
    if (domain.status === 'planned') {
      const timer = window.setTimeout(() => setOpenDomain(domain), 250);
      return () => window.clearTimeout(timer);
    }
  }, []);
  const closeDialog = useCallback(() => setOpenDomain(null), []);
  return <>
      <PageHero eyebrow="ONE PLATFORM. PURPOSE-BUILT WORKSPACES." title="Explore Noetivra in your field." lead="See how connected knowledge becomes practical work. Explore domain-specific experiences for finding evidence, reviewing findings, developing capabilities and coordinating action." body={['Each solution brings together the relevant knowledge, methods, permissions and workflows. Start with a domain, then explore the workspaces designed for its participants and administrators.']} backLink={{
      href: '/noetivra',
      label: 'Noetivra'
    }} data-id="element-2049" data-mp="2x2wfhis78cvw">
        <div className="mt-8 flex flex-col sm:flex-row gap-4" data-id="element-2050" data-mp="dap9531uahv2">
          <a href="#domains" className="inline-flex items-center justify-center gap-2 bg-navy hover:bg-navy-light text-white font-semibold px-6 py-3.5 rounded-md transition-colors duration-150 whitespace-nowrap" data-id="element-2051" data-mp="d83172b1e0cb">
            Choose a domain
            <ArrowDown size={18} aria-hidden="true" data-id="element-2052" data-mp="r12h35eqdwke" />
          </a>
          <a href={governmentDemoUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 border border-line text-navy hover:bg-surface-muted font-semibold px-6 py-3.5 rounded-md transition-colors duration-150 whitespace-nowrap" data-id="element-2053" data-mp="3qhh7wn0pz1i0">
            Open Government Demo
            <ArrowUpRight size={18} aria-hidden="true" data-id="element-2054" data-mp="1er6kpcrx5wbe" />
            <span className="sr-only" data-id="element-2055" data-mp="1eqn0tqipkxuq">(opens in a new tab)</span>
          </a>
        </div>
      </PageHero>

      <GovernmentFeature data-id="element-2056" data-mp="1joolugzp378u" />

      <section id="domains" aria-labelledby="domains-title" className="scroll-mt-20 bg-white border-b border-line py-20 lg:py-28" data-id="element-2057" data-mp="1js2jjbxeys9y">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2058" data-mp="1outnknfujrom">
          <motion.div {...fadeUp} className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-16 mb-12" data-id="element-2059" data-mp="1oxfxuuu4glaj">
            <div className="lg:col-span-5" data-id="element-2060" data-mp="1g2rr108wevjm">
              <p className="text-sm font-medium text-teal mb-3" data-id="element-2061" data-mp="1g1qbaigxtkvk">Choose a domain</p>
              <h2 id="domains-title" className="font-heading font-bold text-3xl lg:text-4xl text-ink-dark tracking-tight leading-tight" data-id="element-2062" data-mp="1b3ymvyfi4gw4">
                Seven solutions, one workspace platform
              </h2>
            </div>
            <dl className="lg:col-span-7 lg:pt-8 grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-2063" data-mp="1160zte9doq53">
              {(['interactive', 'planned'] as const).map(status => <div key={status} data-id="element-2064" data-mp="2vk1xhvt6z73l">
                  <dt className="font-heading font-semibold text-ink-dark" data-id="element-2065" data-mp="2vj0acsywiu5i">{demoStatusLabels[status].label}</dt>
                  <dd className="mt-1 text-ink-muted leading-relaxed" data-id="element-2066" data-mp="2riq2tbvgnvoj">{demoStatusLabels[status].meaning}</dd>
                </div>)}
            </dl>
          </motion.div>

          <ul className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6" data-id="element-2067" data-mp="33jptcz7hhfzt">
            {demoDomains.map(domain => <li key={domain.id} data-id="element-2068" data-mp="2pmjg1k5j49hz">
                <DomainCard domain={domain} highlighted={highlightedId === domain.id} onExplore={selected => {
              setHighlightedId(selected.id);
              setOpenDomain(selected);
            }} data-id="element-2069" data-mp="2tlio3dmk4mdn" />
              </li>)}
          </ul>
        </div>
      </section>

      <DemoFoundation data-id="element-2070" data-mp="2avk7ua8lvmyx" />

      <DomainDetailDialog domain={openDomain} onClose={closeDialog} data-id="element-2071" data-mp="2av0xbelac0ux" />
    </>;
}